import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Typography } from "@material-ui/core";
import { useAuth } from "../../../../contexts/AuthContext";
import AgentReport from "./shared/agent-report/agent-report.shared";
import * as API from "../../../../api";

/**
 * Displays a report of uninstalled orders for a given dealer code, supplier, and date range.
 *
 * @param {object} props - The component props.
 * @param {string} props.supplierPrettyName - The supplier name.
 * @param {boolean} props.impersonation - Whether the component is being impersonated.
 * @param {string} props.impersonationName - The name of the impersonated user.
 */
export default function FibrePeriodicOrderReport({
  supplierPrettyName,
  impersonation,
  impersonationName,
}) {
  // Retrieve the current user from the authentication context
  const { currentUser } = useAuth();

  // Get supplierId from params
  const { supplierId } = useParams();

  // Set the initial dealer code to the current user's dealer code
  const [dealerCode, setDealerCode] = useState(currentUser.id);

  // Set the initial parameters, loading state, data, and customer modal state
  const [showLoading, setShowloading] = useState(false);
  const [data, setData] = useState([]);

  /**
   * Fetches the data from the server.
   * @returns {Promise<void>} - A promise that resolves when the data is fetched.
   */
  const getData = async () => {
    setShowloading(true);
    // Call the server API to get the agent report

    const { data: orderData, error } = await API.getOrderReport({
      user_id: dealerCode,
      supplier_id: supplierId,
      report: "uninstalled",
    });
    if (error) console.error(error);

    setData(orderData);
    setShowloading(false);
  };

  // Fetch data when the dealer code, start date, or end date changes
  useEffect(() => {
    if (dealerCode && supplierId) {
      getData();
    }
  }, [dealerCode, supplierId]);

  // Update the dealer code when the impersonation changes or when the component mounts
  useEffect(() => {
    if (impersonation) {
      setDealerCode(impersonation);
    } else {
      setDealerCode(currentUser.id);
    }
  }, [impersonation]);

  return (
    <div>
      {/* Display the dealer code and impersonation name */}
      <Typography variant="h6">
        Uninstalled Order Report for{" "}
        {impersonationName
          ? `: ${impersonationName}`
          : `: Z-${dealerCode} (You)`}
      </Typography>

      {/* Display the supplier name */}
      <Typography variant="h6">Service: {supplierPrettyName}</Typography>
      <AgentReport data={data} loading={showLoading} />
    </div>
  );
}
